/**
 * Types for the global Sparky copilot (spec-29). These mirror the backend
 * Pydantic schemas in `backend/app/sparky/schemas.py` one-to-one. UUIDs are
 * strings.
 */

/** What the conversation's frozen context snapshot contains (spec §2/§5). */
export type SparkyContextKind = 'deal' | 'board' | 'page'

export type SparkyModelKey = 'sonnet' | 'opus'

/** One option of `GET /sparky/models` (`SparkyModelOut`). */
export interface SparkyModelOption {
  key: SparkyModelKey
  label: string
  hint: string | null
}

/** One file in a conversation (`AttachmentOut`). */
export interface SparkyAttachment {
  id: string
  filename: string
  content_type: string
  size_bytes: number
  input_kind: 'pdf' | 'image' | 'text'
  send_as: 'document' | 'image' | 'text'
  page_count: number | null
  truncated: boolean
  message_id: string | null
  saved_document_id: string | null
  saved_deal_name: string | null
}

/** `POST /attachments/{id}/save` and `/messages/{id}/save` result (`SaveResult`). */
export interface SparkySaveResult {
  document_id: string
  deal_id: string
  folder_id: string | null
  startup_name: string
}

export type SparkyRunStatus = 'queued' | 'running' | 'completed' | 'failed'

/** One conversation in the sidebar (`ConversationOut`). */
export interface SparkyConversation {
  id: string
  title: string | null
  context_kind: SparkyContextKind
  context_deal_id: string | null
  context_route: string | null
  /** 'sonnet' or 'opus': the model of the next answer (spec-78). */
  model_choice?: SparkyModelKey
  /** Name of the deal's startup, when the API provides it (badge "Deal: <naam>"). */
  context_deal_name?: string | null
  created_at: string
  updated_at: string
}

/** One resolved source citation on an assistant turn (`MessageCitation`). */
export interface SparkyCitation {
  tag: string
  kind: string
  id: string | null
  label: string
}

/** One chat message (`MessageOut`). */
export interface SparkyMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  citations: SparkyCitation[] | null
  /** From the producing run — true when Sparky flagged doubt (spec §6). */
  confidence_low: boolean | null
  run_id: string | null
  /** Label of the model that gave this answer (assistant turns only). */
  model_label?: string | null
  attachments?: SparkyAttachment[]
  created_at: string
}

/** `POST /conversations/{id}/messages` 202 body (`PostMessageResult`). */
export interface PostSparkyMessageResponse {
  conversation_id: string
  user_message_id: string
  assistant_message_id: string
  run_id: string
}

/** `GET /conversations/{id}/runs/{run_id}` poll shape (`RunOut`). */
export interface SparkyRunDetail {
  id: string
  conversation_id: string
  status: SparkyRunStatus
  current_step: string | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
  failed_at: string | null
}

/** `POST /conversations` request body. */
export interface CreateConversationBody {
  kind: SparkyContextKind
  deal_id?: string | null
  route?: string | null
  model_choice?: SparkyModelKey
}

/** Raised on a 503 `sparky_disabled` — the feature flag is off (spec §9). */
export class SparkyDisabledError extends Error {
  constructor() {
    super('sparky_disabled')
    this.name = 'SparkyDisabledError'
  }
}

/** Raised on a 409 `run_active` — one active run per conversation (spec §4). */
export class SparkyRunActiveError extends Error {
  constructor() {
    super('run_active')
    this.name = 'SparkyRunActiveError'
  }
}

/** A failed upload or save, carrying the API's Dutch `error` text and code. */
export class SparkyApiError extends Error {
  readonly status: number
  readonly code: string | null
  constructor(message: string, status: number, code: string | null) {
    super(message)
    this.name = 'SparkyApiError'
    this.status = status
    this.code = code
  }
}
